import React from 'react';
import { useTranslation } from 'react-i18next';

import { GraduationCap, Mail, MousePointer2, Wand2 } from 'lucide-react';

import TamanLogo from '../../../assets/TamanLogo.svg?react';
import { TIP_IMAGES } from './tipImages';
import type { TipId } from './tipsProgress';

// Highlight positions below are percentages of the ui* crops in tipImages; adjust them with a re-capture.

const MOD_KEY = /Mac|iPhone|iPad/.test(navigator.userAgent) ? '⌘' : 'Ctrl';

const STYLES = `
  @keyframes tipv-zoom { from { transform: scale(1.08); } to { transform: scale(1); } }
  @keyframes tipv-rise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
  @keyframes tipv-pop { from { opacity: 0; transform: scale(0.85); } to { opacity: 1; transform: none; } }
  @keyframes tipv-pulse {
    0% { box-shadow: 0 0 0 0 rgba(179, 236, 73, 0.55); }
    70% { box-shadow: 0 0 0 12px rgba(179, 236, 73, 0); }
    100% { box-shadow: 0 0 0 0 rgba(179, 236, 73, 0); }
  }
  @keyframes tipv-tap { 0%, 100% { transform: translate(0, 0) scale(1); } 50% { transform: translate(-3px, -3px) scale(0.92); } }
  @keyframes tipv-progress { from { width: 18%; } to { width: 86%; } }
  .tipv-zoom { animation: tipv-zoom 9s ease-out both; }
  .tipv-rise { animation: tipv-rise 0.55s 0.12s cubic-bezier(0.2, 0.8, 0.2, 1) both; }
  .tipv-pop { animation: tipv-pop 0.4s cubic-bezier(0.2, 0.8, 0.2, 1) both; }
  .tipv-ring { border: 2px solid #B3EC49; border-radius: 8px; animation: tipv-pulse 1.8s 0.6s ease-out infinite; }
  .tipv-tap { animation: tipv-tap 1.6s 0.9s ease-in-out infinite; }
  .tipv-progress { animation: tipv-progress 3.2s 0.5s ease-in-out infinite alternate; }
  .tipv-pop-pulse { animation: tipv-pop 0.4s 0.3s both, tipv-pulse 1.8s 0.8s ease-out infinite; }
  @media (prefers-reduced-motion: reduce) {
    .tipv-zoom, .tipv-rise, .tipv-pop, .tipv-pop-pulse, .tipv-ring, .tipv-tap, .tipv-progress { animation: none; }
  }
`;

/** A crop of the real app in a small window frame, so it reads as "this is where it is". */
const AppWindow: React.FC<{ src: string; className: string; children?: React.ReactNode }> = ({
  src,
  className,
  children,
}) => (
  <div
    className={`tipv-rise absolute overflow-hidden rounded-lg border border-white/15 bg-[#111] shadow-[0_18px_50px_rgba(0,0,0,0.65)] ${className}`}
  >
    <div className="flex h-3.5 items-center gap-1 border-b border-white/10 bg-[#1a1a1a] px-2">
      {[0, 1, 2].map((i) => (
        <span key={i} className="h-1.5 w-1.5 rounded-full bg-white/20" />
      ))}
    </div>
    <div className="relative">
      <img src={src} alt="" className="block w-full" draggable={false} />
      {children}
    </div>
  </div>
);

/** Pulsing frame over the part of a crop the tip talks about; values are % of the crop. */
const Ring: React.FC<{ left: number; top: number; width: number; height: number; round?: boolean }> = ({
  left,
  top,
  width,
  height,
  round,
}) => (
  <span
    className="tipv-ring pointer-events-none absolute"
    style={{
      left: `${left}%`,
      top: `${top}%`,
      width: `${width}%`,
      height: `${height}%`,
      borderRadius: round ? '9999px' : undefined,
    }}
  />
);

const Keys: React.FC<{ keys: string[]; label: string; delay: number }> = ({ keys, label, delay }) => (
  <div
    className="tipv-pop flex items-center gap-1 rounded-md border border-white/10 bg-black/60 px-1.5 py-1 backdrop-blur-sm"
    style={{ animationDelay: `${delay}s` }}
  >
    {keys.map((k) => (
      <kbd
        key={k}
        className="min-w-[18px] rounded border border-white/25 bg-white/10 px-1 text-center font-sans text-[11px] font-semibold leading-[18px] text-white"
      >
        {k}
      </kbd>
    ))}
    <span className="ml-0.5 text-[11px] font-semibold text-[#B3EC49]">{label}</span>
  </div>
);

const Background: React.FC<{ src: string; mirrored?: boolean }> = ({ src, mirrored }) => (
  <img
    src={src}
    alt=""
    draggable={false}
    className="tipv-zoom absolute inset-0 h-full w-full object-cover"
    style={mirrored ? { scale: '-1 1' } : undefined}
  />
);

/** Header artwork for one tip: illustration, plus where useful a framed crop of the app with the spot highlighted. */
export const TipVisual: React.FC<{ tipId: TipId }> = ({ tipId }) => {
  const { t } = useTranslation();

  const content = (() => {
    switch (tipId) {
      case 'welcome':
        return (
          <>
            <Background src={TIP_IMAGES.welcome} />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/20 to-transparent" />
            <div className="tipv-rise absolute left-[6%] top-1/2 -translate-y-1/2">
              <TamanLogo className="h-5 w-auto text-white sm:h-6" />
              <div className="mt-2 h-[2px] w-10 rounded-full bg-[#B3EC49]" />
            </div>
          </>
        );
      case 'examples':
        return (
          <>
            <Background src={TIP_IMAGES.bokeh} />
            <AppWindow src={TIP_IMAGES.uiExamples} className="left-[7%] top-[11%] w-[86%]">
              <Ring left={20.3} top={17} width={59} height={62} />
              <MousePointer2
                className="tipv-tap pointer-events-none absolute h-5 w-5 fill-white text-black drop-shadow"
                style={{ left: '31%', top: '50%' }}
              />
            </AppWindow>
          </>
        );
      case 'layers':
        return (
          <>
            <Background src={TIP_IMAGES.layers} />
            <AppWindow src={TIP_IMAGES.uiLayers} className="right-[4%] top-[10%] w-[52%]">
              <Ring left={63.5} top={19} width={24} height={80} />
            </AppWindow>
            <div className="absolute bottom-[9%] left-[4%] flex flex-col gap-1.5">
              <Keys keys={[MOD_KEY, '⇧', '1']} label="CHL" delay={0.7} />
              <Keys keys={[MOD_KEY, '⇧', '2']} label="LMN" delay={0.85} />
            </div>
          </>
        );
      case 'optimize':
        return (
          <>
            <Background src={TIP_IMAGES.optimize} />
            <div className="absolute inset-0 bg-gradient-to-r from-black/55 to-transparent" />
            <div className="absolute bottom-[12%] left-[5%] flex flex-col items-start gap-2">
              <div className="tipv-pop-pulse flex items-center gap-2 rounded-full border border-[#B3EC49] bg-black/70 px-3.5 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
                <Wand2 size={14} className="text-[#B3EC49]" />
                {t('tips.visual.autoposition')}
              </div>
              <div
                className="tipv-pop w-[180px] rounded-md border border-white/10 bg-black/60 px-2.5 py-1.5 backdrop-blur-sm"
                style={{ animationDelay: '0.55s' }}
              >
                <div className="text-[11px] font-medium text-white/75">{t('tips.visual.optimizing')}</div>
                <div className="mt-1 h-1 overflow-hidden rounded-full bg-white/10">
                  <div className="tipv-progress h-full rounded-full bg-[#B3EC49]" />
                </div>
              </div>
            </div>
          </>
        );
      case 'market':
        return (
          <>
            <Background src={TIP_IMAGES.market} />
            {[
              { left: '9%', score: 97, best: true, delay: 0.4 },
              { left: '55%', score: 94, best: false, delay: 0.6 },
            ].map((chip) => (
              <div
                key={chip.score}
                className={`tipv-pop absolute top-[10%] flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold shadow-lg ${
                  chip.best
                    ? 'bg-[#B3EC49] text-black'
                    : 'border border-white/20 bg-black/70 text-white backdrop-blur-sm'
                }`}
                style={{ left: chip.left, animationDelay: `${chip.delay}s` }}
              >
                {t('tips.visual.match', { score: chip.score })}
                {chip.best && <span className="font-medium opacity-70">· {t('tips.visual.bestMatch')}</span>}
              </div>
            ))}
          </>
        );
      case 'help':
        return (
          <>
            <Background src={TIP_IMAGES.bokeh} mirrored />
            <AppWindow src={TIP_IMAGES.uiToolbar} className="left-[8%] top-[8%] w-[27%]">
              <Ring left={7.5} top={82} width={14.5} height={11.5} round />
            </AppWindow>
            <div className="absolute right-[7%] top-1/2 flex -translate-y-1/2 flex-col items-start gap-2">
              <div
                className="tipv-pop flex items-center gap-2 rounded-full border border-white/15 bg-black/65 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-sm"
                style={{ animationDelay: '0.5s' }}
              >
                <GraduationCap size={14} className="text-[#B3EC49]" />
                {t('navbar.tutorials')}
              </div>
              <div
                className="tipv-pop flex items-center gap-2 rounded-full border border-white/15 bg-black/65 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-sm"
                style={{ animationDelay: '0.65s' }}
              >
                <Mail size={14} className="text-[#B3EC49]" />
                help@taman.tech
              </div>
            </div>
          </>
        );
    }
  })();

  return (
    // Keyed by tip so the entrance animations replay on every switch.
    <div
      key={tipId}
      className="relative aspect-[21/9] w-full overflow-hidden rounded-t-xl bg-[#111]"
      aria-hidden="true"
    >
      <style>{STYLES}</style>
      {content}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-[#1c1c1c] to-transparent" />
    </div>
  );
};
